import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CaretDown, MagnifyingGlass, Plus} from '../../icons';
import {Spinner} from '../../Spinner';
import {initialsOf, pickerOptions} from './accountsModel';

// Lo que mide el panel abierto (`.aac-picker` en `_agency-accounts-v2.scss`): decide si abre hacia
// arriba y de qué lado se alinea. Si cambia el SCSS, cambia acá.
const PANEL_HEIGHT = 380;
const PANEL_WIDTH = 300;
const MARGIN = 12;

export const CLIENTS_LOADING = 'loading';
export const CLIENTS_OK = 'ok';
export const CLIENTS_FAILED = 'failed';

export function ClientAvatar({name}) {
    return <span className="aac-avatar" aria-hidden="true">{initialsOf(name) || '·'}</span>;
}

/**
 * El panel del buscador de clientes: la sugerencia por nombre arriba, todos los clientes de la
 * agencia debajo y «Crear un cliente nuevo» al pie.
 *
 * Se dibuja ABSOLUTO dentro de su ancla, no porteado: la lista no recorta (`overflow: visible`), y
 * así el panel acompaña a su fila al desplazar. Lo que sí hace falta es elegir el lado: en la última
 * fila de la pantalla, abierto hacia abajo quedaba fuera de la ventana; mide el espacio y abre hacia
 * arriba si abajo no entra, y se alinea a la izquierda si alineado a la derecha se saldría.
 */
function ClientPickerPanel({anchorRef, clients, clientsState, suggested, excludeIds, onPick, onCreate, onClose}) {
    const {t} = useTranslation();
    const [query, setQuery] = useState('');
    const [placement, setPlacement] = useState({up: false, left: false});
    const inputRef = useRef(null);

    useLayoutEffect(() => {
        const anchor = anchorRef.current;
        if (!anchor || !anchor.getBoundingClientRect) return;
        const rect = anchor.getBoundingClientRect();
        const below = window.innerHeight - rect.bottom;
        setPlacement({
            up: below < PANEL_HEIGHT + MARGIN && rect.top > below,
            left: rect.right - PANEL_WIDTH < MARGIN,
        });
    }, [anchorRef]);

    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    // Un clic afuera cierra. `mousedown` y no `click`: el clic que abre otro buscador cierra éste
    // antes de que el otro se abra, y nunca quedan dos abiertos.
    useEffect(() => {
        const handleDown = (event) => {
            const anchor = anchorRef.current;
            if (anchor && anchor.contains(event.target)) return;
            onClose({restoreFocus: false});
        };
        document.addEventListener('mousedown', handleDown);
        return () => document.removeEventListener('mousedown', handleDown);
    }, [anchorRef, onClose]);

    // Escape cierra SÓLO el buscador: se frena acá para que no le llegue al documento, donde el
    // drawer escucha su propio Escape.
    const handleKeyDown = (event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onClose({restoreFocus: true});
    };

    const options = useMemo(
        () => pickerOptions(clients, query, suggested, excludeIds),
        [clients, query, suggested, excludeIds],
    );

    const renderItem = (client, isSuggested) => (
        <button key={`${isSuggested ? 's' : 'c'}-${client.id}`} type="button"
                className={`aac-picker__item${isSuggested ? ' -suggested' : ''}`}
                onClick={() => onPick(client)}>
            <ClientAvatar name={client.name}/>
            <span className="aac-picker__name">{client.name}</span>
            {isSuggested && <span className="aac-picker__why">{t('agency_v2.accounts.picker_suggested_why')}</span>}
        </button>
    );

    const renderList = () => {
        if (clientsState === CLIENTS_LOADING) {
            return <div className="box-loader"><Spinner color="positive small"/></div>;
        }
        if (clientsState === CLIENTS_FAILED) {
            return <p className="aac-picker__empty">{t('clients_v2.action_error')}</p>;
        }
        if (!options.suggested && !options.others.length) {
            return <p className="aac-picker__empty">{t('onboarding.clients.assign_no_matches')}</p>;
        }
        return (
            <>
                {options.suggested && (
                    <>
                        <div className="aac-picker__title">{t('agency_v2.accounts.picker_suggested')}</div>
                        {renderItem(options.suggested, true)}
                    </>
                )}
                {options.others.length > 0 && (
                    <div className="aac-picker__title">{t('agency_v2.accounts.picker_all')}</div>
                )}
                {options.others.map((client) => renderItem(client, false))}
            </>
        );
    };

    return (
        <div className={`aac-picker${placement.up ? ' -opens-up' : ''}${placement.left ? ' -align-left' : ''}`}
             role="dialog" aria-label={t('agency_v2.accounts.picker_label')} onKeyDown={handleKeyDown}
             data-testid="aac-picker">
            <label className="aac-search -picker">
                <MagnifyingGlass size={15} aria-hidden="true"/>
                <input ref={inputRef} type="text" value={query}
                       onChange={(event) => setQuery(event.target.value)}
                       placeholder={t('agency_v2.accounts.picker_search')}
                       aria-label={t('agency_v2.accounts.picker_search')}/>
            </label>
            <div className="aac-picker__list">{renderList()}</div>
            <button type="button" className="aac-picker__new" onClick={onCreate}>
                <Plus size={14} weight="bold" aria-hidden="true"/>
                {t('agency_v2.accounts.picker_new')}
            </button>
        </div>
    );
}

/**
 * «Conectar a un cliente ▾»: el botón y su buscador. El estado abierto es de cada botón (uno por
 * fila, el de la barra de selección y el «Cambiar de cliente» del drawer); como el `mousedown` de
 * afuera cierra al que esté abierto, nunca hay dos a la vez.
 */
export function ClientPickerButton({label, className = 'aac-btn -sm', clients, clientsState, onOpen, suggested,
                                       excludeIds, onPick, onCreate, disabled = false, busy = false}) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const triggerRef = useRef(null);

    const close = useCallback(({restoreFocus} = {}) => {
        setOpen(false);
        if (restoreFocus && triggerRef.current) triggerRef.current.focus();
    }, []);

    const toggle = () => {
        if (!open && onOpen) onOpen();
        setOpen((current) => !current);
    };

    return (
        <span className="aac-pick" ref={anchorRef}>
            <button ref={triggerRef} type="button" className={`${className}${busy ? ' -busy' : ''}`}
                    aria-expanded={open} aria-haspopup="dialog" aria-busy={busy || undefined}
                    disabled={disabled || busy} onClick={toggle}>
                {label}
                <CaretDown size={13} weight="bold" aria-hidden="true"/>
            </button>
            {open && (
                <ClientPickerPanel anchorRef={anchorRef} clients={clients} clientsState={clientsState}
                                   suggested={suggested} excludeIds={excludeIds}
                                   onPick={(client) => { close(); onPick(client); }}
                                   onCreate={() => { close(); onCreate(); }}
                                   onClose={close}/>
            )}
        </span>
    );
}
